import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { submitManuscript, MAX_MANUSCRIPT_BYTES } from '../services/submissionService';
import { 
  FaCloudUploadAlt, 
  FaFilePdf, 
  FaFileWord, 
  FaFileImage, 
  FaFileAlt, 
  FaTimes, 
  FaCheck, 
  FaExclamationTriangle, 
  FaInfoCircle 
} from 'react-icons/fa';

// Several papers can be submitted at once; each one becomes its own submission with its own tracking ID
export const MAX_MANUSCRIPTS = 10;

const formatMb = (bytes) => `${(bytes / 1024 / 1024).toFixed(2)} MB`;

const FileUpload = forwardRef(({ onFileSelected, formData }, ref) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const [progress, setProgress] = useState(0);

  const [uploadingIndex, setUploadingIndex] = useState(0);

  // Submits every selected paper in turn (see services/submissionService.js).
  // Papers already submitted are kept if a later one fails, so nothing is sent twice on a retry.
  const uploadFile = async () => {
    if (selectedFiles.length === 0) {
      setError('No file selected');
      return false;
    }

    setUploading(true);
    setError('');
    const done = [];
    const remaining = [...selectedFiles];

    try {
      while (remaining.length > 0) {
        setUploadingIndex(done.length);
        setProgress(0);
        const result = await submitManuscript(remaining[0], formData || {}, setProgress);
        done.push({ ...result, originalName: result.fileName, size: result.fileSize });
        remaining.shift();
      }
      setUploadedFiles(done);
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // trackingId / emailSent of the first paper kept for callers that show a single one
      return { success: true, data: { ...done[0], submissions: done, emailSent: done.every((d) => d.emailSent) } };
    } catch (err) {
      setSelectedFiles(remaining);
      setUploadedFiles(done);
      const message = done.length > 0
        ? `${done.length} paper(s) were submitted (${done.map((d) => d.trackingId).join(', ')}), but "${remaining[0].name}" failed: ${err.message}`
        : err.message || 'Upload failed.';
      setError(message);
      return { success: false, error: message };
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    if (typeof index === 'number') {
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    } else {
      setSelectedFiles([]);
      setUploadedFiles([]);
    }
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useImperativeHandle(ref, () => ({
    uploadFile,
    selectedFile: selectedFiles[0] || null,
    selectedFiles,
    uploadedFile: uploadedFiles[0] || null,
    uploadedFiles,
    error,
    removeFile
  }));

  const handleFileSelect = (event) => {
    const picked = Array.from(event.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (picked.length === 0) return;

    const problems = [];
    const accepted = [];
    picked.forEach((file) => {
      // Word documents only (checked by extension too: some systems report .doc files without a type)
      if (!/\.(docx?)$/i.test(file.name)) problems.push(`"${file.name}" is not a Word document (.doc or .docx).`);
      else if (file.size > MAX_MANUSCRIPT_BYTES) problems.push(`"${file.name}" is larger than 100MB.`);
      else accepted.push(file);
    });

    const next = [...selectedFiles];
    accepted.forEach((file) => {
      const duplicate = next.some((f) => f.name === file.name && f.size === file.size);
      if (duplicate) return;
      if (next.length >= MAX_MANUSCRIPTS) {
        problems.push(`At most ${MAX_MANUSCRIPTS} papers can be submitted at once; "${file.name}" was not added.`);
        return;
      }
      next.push(file);
    });

    setError(problems.join(' '));
    setUploadedFiles([]);
    setSelectedFiles(next);
    if (onFileSelected && next[0]) {
      onFileSelected(next[0]);
    }
  };

  const renderFileTypeIcon = (fileName) => {
    const extension = (fileName || '').split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FaFilePdf className="text-red-500 text-2xl" />;
      case 'doc':
      case 'docx':
        return <FaFileWord className="text-blue-600 text-2xl" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FaFileImage className="text-emerald-500 text-2xl" />;
      default:
        return <FaFileAlt className="text-gray-500 text-2xl" />;
    }
  };

  return (
    <div className="space-y-4">
      {/* Show upload UI until the papers are submitted */}
      {(uploadedFiles.length === 0 || selectedFiles.length > 0) && (
        <div className="relative">
          <input
            ref={fileInputRef}
            type="file"
            accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            multiple
            onChange={handleFileSelect}
            disabled={uploading}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed z-10"
          />
          <div className="border-2 border-dashed border-blue-200/90 hover:border-[#03478E] bg-[#F8FAFC] hover:bg-blue-50/40 rounded-3xl p-8 sm:p-10 text-center transition-all duration-200 group cursor-pointer">
            <div className="flex flex-col items-center space-y-3.5">
              <div className="w-14 h-14 bg-blue-50 text-[#03478E] rounded-2xl flex items-center justify-center group-hover:scale-105 transition-transform duration-200 shadow-2xs">
                <FaCloudUploadAlt className="text-3xl" />
              </div>
              <div>
                <p className="text-sm sm:text-base font-bold text-gray-800 mb-1">
                  {selectedFiles.length > 0 ? 'Add another paper' : 'Choose your manuscript file(s)'}
                </p>
                <p className="text-xs text-gray-500">
                  Drag and drop your files here, or <span className="text-[#03478E] font-bold underline">browse files</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  You can submit up to {MAX_MANUSCRIPTS} papers at once. Each paper gets its own tracking ID.
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-1.5 text-xs text-gray-500">
                <span className="px-2.5 py-1 bg-white rounded-full shadow-2xs font-medium">DOCX</span>
                <span className="px-2.5 py-1 bg-white rounded-full shadow-2xs font-medium">DOC</span>
              </div>
              <p className="text-xs text-gray-400">
                Maximum file size: 100MB per paper
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Selected papers */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2 animate-fadein">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
            {selectedFiles.length} {selectedFiles.length === 1 ? 'paper' : 'papers'} ready for submission
          </p>
          {selectedFiles.map((file, index) => (
            <div key={`${file.name}-${file.size}`} className="bg-blue-50/60 rounded-2xl p-4 flex items-center justify-between gap-3">
              <div className="flex items-center space-x-3 min-w-0">
                <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-2xs">
                  {renderFileTypeIcon(file.name)}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatMb(file.size)}
                    {uploading && index === 0 ? ' • Uploading...' : ''}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeFile(index)}
                disabled={uploading}
                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-40 rounded-xl transition-colors"
                title="Remove file"
              >
                <FaTimes className="text-sm" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Uploading State */}
      {uploading && (
        <div className="bg-amber-50 rounded-2xl p-4 flex items-center space-x-3">
          <div className="w-10 h-10 bg-amber-500 text-white rounded-xl flex items-center justify-center flex-shrink-0">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
          <div>
            <p className="text-sm font-bold text-amber-900">
              Uploading paper {uploadingIndex + 1} of {uploadingIndex + selectedFiles.length}... {progress}%
            </p>
            <p className="text-xs text-amber-700">Please do not close this window while the file is transferring.</p>
            <div className="mt-2 h-1.5 w-48 max-w-full bg-amber-100 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 rounded-2xl p-4 flex items-center space-x-3">
          <FaExclamationTriangle className="text-red-500 text-lg flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-red-800">Upload Issue</p>
            <p className="text-xs text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Success State */}
      {uploadedFiles.length > 0 && (
        <div className="bg-emerald-50 rounded-3xl p-6 space-y-3 animate-fadein">
          {uploadedFiles.map((file) => (
            <div key={file.trackingId} className="flex items-start space-x-3 min-w-0">
              <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-2xs">
                {renderFileTypeIcon(file.originalName)}
              </div>
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <h4 className="text-sm font-bold text-emerald-900 truncate">
                    {file.originalName}
                  </h4>
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                    <FaCheck className="text-[8px]" /> Submitted
                  </span>
                </div>
                <p className="text-xs text-emerald-700">
                  Size: {formatMb(file.size)}
                </p>
                <p className="text-xs text-emerald-900">
                  Tracking ID: <span className="font-bold tracking-wide">{file.trackingId}</span>
                </p>
              </div>
            </div>
          ))}

          <div className="p-3 bg-white/80 rounded-2xl flex items-center gap-2 text-xs text-emerald-800">
            <FaInfoCircle className="text-emerald-600 flex-shrink-0" />
            <span>Please keep the tracking ID{uploadedFiles.length > 1 ? 's' : ''} for any correspondence with the editorial office.</span>
          </div>
        </div>
      )}
    </div>
  );
});

FileUpload.displayName = 'FileUpload';

export default FileUpload;
